import { isEmpty } from "lodash";
import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import BetSlipContext from "../../context/BetSlipContext";
import Keyboard from "./Keyboard";
import { BetCategoryTabs } from "./MarketTabs";
import AuthContext from "../../context/AuthContext";
import secureLocalStorage from "react-secure-storage";

// --- Injected CSS for flash animation ---
const flashStyle = `
@keyframes sportsbookOddsFlash {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.15); }
}
.sportsbook-odds-flash {
  animation: sportsbookOddsFlash 0.6s ease;
}
`;

// --- Sub-component to track odds changes and flash ---
const OddsCell = ({ res, active = false, marketLimit, card = false }) => {
  const prevOdds = useRef(res?.odds);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    if (prevOdds.current !== res?.odds) {
      prevOdds.current = res?.odds;
      setFlashing(true);
      const timer = setTimeout(() => setFlashing(false), 600);
      return () => clearTimeout(timer);
    }
  }, [res?.odds]);

  const limit = Number(marketLimit);
  const limitLabel = Number.isFinite(limit) && limit > 0
    ? limit.toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 })
    : null;

  return (
    <div
      className={flashing ? "sportsbook-odds-flash" : undefined}
      style={{
        ...sportsbookRowStyles.odds,
        ...(card ? sportsbookRowStyles.cardOdds : {}),
        background: active ? "#10a35e" : "#72e3a0",
        color: active ? "#fff" : "#243a48",
        boxShadow: active ? "inset 0 2px 5px #0004" : "none",
      }}
    >
      <span style={sportsbookRowStyles.oddsValue}>
        {Math.trunc(res.odds * 100) / 100}
      </span>
      {limitLabel && (
        <span
          title={`Market limit: ${limit.toLocaleString("en-US")}`}
          aria-label={`Market limit: ${limit}`}
          style={sportsbookRowStyles.oddsAmount}
        >
          {limitLabel}
        </span>
      )}
    </div>
  );
};
// Premium feeds provide market names without a shared category identifier.
const getSportsbookCategory = (market, gameType) => {
  const name = String(market?.marketName || "")
    .replace(/[_-]/g, " ")
    .toLowerCase();

  if (/\b(?:odds?\s*(?:\/|or|and|&)?\s*evens?|evens?\s*(?:\/|or|and|&)?\s*odds?)\b/.test(name)) {
    return "OddEven";
  }
  if (gameType !== "cricket") return "Match";

  if (/\b(?:bowlers?|bowling|wicket takers?|wickets? taken|take\s+\d+\s+wickets?|runs conceded|maidens?|economy)\b/.test(name)) {
    return "Bowler";
  }
  if (/\b(?:batsm[ae]n|batters?|batting|player|run scorers?|runs scored|to score|centur(?:y|ies)|fift(?:y|ies)|half centur(?:y|ies))\b/.test(name)) {
    return "Batsman";
  }
  if (/\b(?:match|toss|winner|win|tie|tied|draw|teams?|innings)\b/.test(name)) {
    return "Match";
  }
  return "Extra";
};

const PreminumFancyNew = ({
  data,
  betType,
  setSlipObj,
  slipObj,
  details,
  profileData,
  user,
  resetSelection,
  key,
  messaageBox,
  setPremiumFancy,
  preminumFancyTab,
  activeSportBook,
  setActiveSportBook,
}) => {
  const [selected, setSelected] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [infoMarketId, setInfoMarketId] = useState(null);
  const { parly, parlyBet, setParlyBet } = useContext(AuthContext);
  let { betSelectionObj, setBetSelectionObj, defaultStake, defaultOdds } =
    useContext(BetSlipContext);
  const [amountRange, setAmountRange] = useState({
    min: 0,
    max: 0,
    oddsLimit: 0,
    betDelay: 0,
  });

  useEffect(() => {
    setSelectedCategory("All");
  }, [details?.eventId]);

  useEffect(() => {
    setInfoMarketId(null);
  }, [selectedCategory, details?.eventId]);

  // Local display data with suspend/remove logic
  const [displayData, setDisplayData] = useState(data);
  const suspensionTimersRef = useRef({});
  const prevDataRef = useRef(data);

  useEffect(() => {
    const prevIds = new Set(prevDataRef.current.map(item => item.id));
    const newIds = new Set(data.map(item => item.id));

    // Items that disappeared — suspend then remove after 5s
    prevDataRef.current.forEach(prevItem => {
      if (!newIds.has(prevItem.id) && !suspensionTimersRef.current[prevItem.id]) {
        // Mark suspended
        setDisplayData(current =>
          current.map(item =>
            item.id === prevItem.id
              ? {
                  ...item,
                  apiSiteStatus: "SUSPENDED",
                  markets: { ...item.markets, apiSiteStatus: "SUSPENDED" },
                  is_enable: false,
                }
              : item
          )
        );

        // Remove after 5 seconds
        suspensionTimersRef.current[prevItem.id] = setTimeout(() => {
          setDisplayData(current => current.filter(item => item.id !== prevItem.id));
          delete suspensionTimersRef.current[prevItem.id];
        }, 5000);
      }
    });

    // Items returning — cancel suspension timer
    data.forEach(newItem => {
      if (suspensionTimersRef.current[newItem.id]) {
        clearTimeout(suspensionTimersRef.current[newItem.id]);
        delete suspensionTimersRef.current[newItem.id];
      }
    });

    // Merge incoming fresh data with any currently suspended items
    setDisplayData(current => {
      const suspendedItems = current.filter(
        item => !newIds.has(item.id) && item.apiSiteStatus === "SUSPENDED"
      );
      return [...suspendedItems, ...data];
    });

    prevDataRef.current = data;
  }, [data]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(suspensionTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    let obj = profileData?.matchSetting?.find(
      (res) =>
        res?.type == "sportBook" &&
        (profileData?.arrayCheck == "details"
          ? true
          : res?.sportType == details?.gameType),
    );
    if (!isEmpty(obj)) {
      setAmountRange({
        min: Number(obj?.minAmount) || 0,
        max: Number(obj?.maxAmount) || 0,
        oddsLimit: obj.oddsLimit ? obj.oddsLimit : 0,
        betDelay: obj?.betDelay,
      });
    } else {
      // Feed-sourced events have no sportBook entry in profileData.matchSetting,
      // so amountRange stayed {min:0,max:0} and the Place Bet button was
      // permanently disabled (bidPrice <= max=0 is always false). Fall back to
      // the market's own min/max from the match-detail feed so premium bets
      // can actually be placed.
      setAmountRange({
        min: Number(details?.minAmount) || 10,
        max: Number(details?.maxAmount) || 100000,
        oddsLimit: 0,
        betDelay: 0,
      });
    }
  }, [profileData, user, details]);

  const myRef = useRef(null);
  const [id, setId] = useState("");
  const [id2, setId2] = useState("");

  const handleScroll = () => {
    setId("");
    setId2("");
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(id)) {
      myRef.current.scrollIntoView(true);
    }
  }, [id]);

  const [innerId, setInnerId] = useState("");

  useEffect(() => {
    if (key == "Fancy Bet") {
      setInnerId("");
      setSelected("");
    }
  }, [key]);

  useEffect(() => {
    if (selected && betSelectionObj?.betType == "premiumFancy") {
      const selectedItem = displayData?.find((item) => item?.id == selected);
      if (selectedItem) {
        const isSuspended =
          details?.sport_setting?.status == "inActive" ||
          selectedItem.marketStatus == 3 ||
          selectedItem.marketStatus == 2 ||
          selectedItem.marketStatus == 4 ||
          selectedItem.apiSiteStatus == "SUSPENDED";

        if (isSuspended) {
          resetSelection(betType);
          setSelected("");
          setInnerId("");
          setSlipObj({});
        }
      }
    }
  }, [displayData, details?.sport_setting?.status, selected, betSelectionObj?.betType]);

  useEffect(() => {
    setPremiumFancy((current) => {
      return current?.map((res, index) => {
        return { ...res, check: index < 5 ? true : false };
      });
    });
  }, []);

  useEffect(() => {
    if (details?.gameType == "cricket") {
      data.sort(function (a, b) {
        if (a.marketName === "Match Winner") return -1;
        if (a.marketName === "Will there be a tie") return 1;
        return 0;
      });
    }
  }, [details]);

  const [newActive, setNewActive] = useState([]);
  useEffect(() => {
    setNewActive(
      data?.slice(0, 50)?.map((po) => po?.id),
    );
  }, [data]);

  const allowedSoccerMarkets = [
    "Match Result",
    "Second Half Total Goals Over / Under 0.50",
    "Second Half Total Goals Over / Under 0.75",
    "Second Half Total Goals Over / Under 1.00",
    "Second Half Total Goals Over / Under 1.25",
    "Second Half Total Goals Over / Under 1.50",
    "Total Goals Over / Under 2.25",
    "Second Half Draw No Bet",
    "Odd Or Even Goals",
    "No Draw Both Teams To Score - Yes/No",
    "Total Goals Over / Under 1.25",
    "Total Goals Over / Under 1.50",
    "Total Goals Over / Under 2.50",
    "Draw No Bet",
    "Total Goals Over / Under 2.75",
    "Half Time Result",
    "Second Half Match Result",
    "Half Time First Team To Score",
    "Exact Number of Goals",
    "Odd Or Even Total",
    "Both Teams To Score 2 or More Goals",
    "Correct Score",
  ];

  const filteredData =
    details?.gameType === "soccer"
      ? displayData
          ?.filter((item) => allowedSoccerMarkets.includes(item?.marketName))
          ?.sort(
            (a, b) =>
              allowedSoccerMarkets.indexOf(a?.marketName) -
              allowedSoccerMarkets.indexOf(b?.marketName),
          )
      : displayData?.slice().sort((a, b) => {
          const topMarkets = ["Match Winner", "Match Result"];
          const aIsTop = topMarkets.includes(a?.marketName);
          const bIsTop = topMarkets.includes(b?.marketName);
          if (aIsTop && !bIsTop) return -1;
          if (!aIsTop && bIsTop) return 1;
          return 0;
        });

  const categoryData = (filteredData || []).filter(
    (item) =>
      item?.sportsBookSelection?.length > 0 &&
      (selectedCategory === "All" ||
        getSportsbookCategory(item, details?.gameType) === selectedCategory),
  );

  return (
    <>
      <style>{flashStyle}</style>
      <BetCategoryTabs
        tabs={[
          { type: "All", name: "All" },
          { type: "Match", name: "Match" },
          { type: "OddEven", name: "Odds/Evens" },
          { type: "Batsman", name: "Batsman" },
          { type: "Bowler", name: "Bowler" },
          { type: "Extra", name: "Extra" },
        ]}
        active={selectedCategory}
        onSelect={setSelectedCategory}
        label="Sportsbook categories"
        variant="sportsbook"
      />

      {categoryData.length === 0 && (
        <div id="sportsBookNoBet" className="bets-wrap" style={{ display: "block" }}>
          <p className="no-game" role="status">
            {selectedCategory === "All"
              ? "Sports Book has no market"
              : "No markets available in this category"}
          </p>
        </div>
      )}

      {categoryData.map((item) => {
        if (item?.sportsBookSelection?.length > 0 && details?.gameType) {
          const activeSelections = item.sportsBookSelection.filter((res) => res?.isActive === 1);
          const selectionNames = item.sportsBookSelection.map((res) =>
            String(res?.selectionName || "").trim().toUpperCase()
          );
          const isOddEven = selectionNames.length === 2 &&
            selectionNames.includes("ODD") && selectionNames.includes("EVEN");
          const isExpanded = [
            ...newActive,
            ...(activeSportBook?.length > 0 ? activeSportBook : []),
          ].includes(item.id);
          const betslip = isExpanded &&
            betSelectionObj?.priceToogle &&
            betSelectionObj?.betType == "premiumFancy" &&
            innerId == slipObj?.selectId &&
            item.id == selected &&
            activeSelections.some((res) => res.id == innerId) ? (
              <section
                aria-label="Sportsbook betslip"
                style={{ flex: "0 0 100%", width: "100%", minWidth: 0, margin: 0, padding: 0 }}
              >
                <Keyboard
                  setSlipObj={setSlipObj}
                  details={details}
                  slipObj={slipObj}
                  amountRange={amountRange}
                  betType={betType}
                  resetSelection={resetSelection}
                />
              </section>
            ) : null;

          return (
            <div
              id="sportsBookMarket_2_2558118_265869284"
              className="bets-wrap sports-book new-premimum-fancy"
              key={item.id}
            >
              <h4
                ref={item?.id == id ? myRef : null}
                onClick={() => {
                  setSelected(item?.id);
                  setActiveSportBook((current) => {
                    if (newActive?.includes(item?.id)) {
                      setNewActive((prev) =>
                        prev?.filter((res) => res !== item?.id)
                      );
                    } else {
                      if (current?.includes(item?.id)) {
                        return current?.filter((res) => res !== item?.id);
                      } else {
                        return current?.length > 0
                          ? [...current, item?.id]
                          : [item?.id];
                      }
                    }
                  });
                }}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "24px",
                  minHeight: "24px",
                  lineHeight: "24px",
                  boxSizing: "border-box",
                  padding: "0 7px",
                  marginTop: "3px",
                  marginBottom: "0",
                  background: "linear-gradient(180deg, #ff6c17 0%, #d94c00 100%)",
                  color: "#fff",
                  borderRadius: "5px",
                }}
              >
                <span
                  id="marketName"
                  className="to-collapse"
                  title={item?.marketName}
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                    lineHeight: "24px",
                  }}
                >
                  {item?.marketName}
                </span>
                <button
                  type="button"
                  aria-label={`${item?.marketName} limits`}
                  aria-expanded={infoMarketId === item.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    setInfoMarketId((current) => current === item.id ? null : item.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setInfoMarketId(null);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 24px",
                    width: "24px",
                    height: "24px",
                    margin: 0,
                    padding: 0,
                    border: 0,
                    background: "transparent",
                    boxShadow: "none",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
                    <circle cx="10" cy="10" r="9" fill="currentColor" />
                    <path fill="#ed5908" d="M9.02 6.995V5.577h1.96v1.418H9.02zM9.02 14.42V8.46h1.96v5.96H9.02z" />
                  </svg>
                </button>
                {infoMarketId === item.id && (
                  <div
                    className="fancy_info-popup"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setInfoMarketId(null);
                    }}
                    style={{ display: "flex", top: "100%", right: "7px", lineHeight: "normal" }}
                  >
                    <dl>
                      <dt>Min / Max</dt>
                      <dd>
                        {amountRange?.min?.toFixed(2)} /{" "}
                        {amountRange?.max?.toFixed(2)}
                      </dd>
                    </dl>
                    <button
                      type="button"
                      className="close"
                      aria-label="Close limits"
                      onClick={() => setInfoMarketId(null)}
                    >
                      Close
                    </button>
                  </div>
                )}
              </h4>

              <div style={{
                ...sportsbookRowStyles.list,
                ...(isOddEven && isExpanded ? sportsbookRowStyles.oddEvenList : {}),
              }}>
                {isExpanded && activeSelections.map((res) => {
                      let newPosition = "";
                      if (
                        betSelectionObj?.priceToogle &&
                        betSelectionObj?.betType == "premiumFancy" &&
                        innerId == slipObj?.selectId &&
                        item?.id == selected
                      ) {
                        let backProfit =
                          (Number(betSelectionObj?.oddsSelect) - 1) *
                          Number(betSelectionObj?.bidPrice);
                        let backLoss = Number(betSelectionObj?.bidPrice);
                        if (res.id === slipObj?.selectId) {
                          newPosition =
                            Math.abs(res?.position) > 0
                              ? Number(backProfit) + Number(res.position)
                              : Number(backProfit);
                        } else {
                          newPosition =
                            Math.abs(res?.position) > 0
                              ? Number(backLoss) - Number(res?.position)
                              : -Number(backLoss);
                        }
                      }

                      let condition =
                        betSelectionObj?.priceToogle &&
                        betSelectionObj?.betType == "premiumFancy" &&
                        innerId == slipObj?.selectId &&
                        item?.id == selected &&
                        Number(betSelectionObj?.bidPrice) > 0;

                      const isSuspended =
                        details?.sport_setting?.status == "inActive" ||
                        item.marketStatus == 3 ||
                        item.marketStatus == 2 ||
                        item.marketStatus == 4 ||
                        item.apiSiteStatus == "SUSPENDED";

                      const isSelected = !isSuspended && (parly
                        ? !isEmpty(user) && parlyBet?.some((bet) =>
                            bet.newEventId == details?.eventId &&
                            bet.id == item.id &&
                            bet.sportsBookSelectionObject?.id == res.id)
                        : betSelectionObj?.priceToogle &&
                          betSelectionObj?.betType === betType &&
                          item.id == selected && res.id == innerId);
                      const showProjectedPosition = !parly && condition;
                      const displayedPosition = Number(showProjectedPosition ? newPosition : res.position);
                      const showPosition = Number.isFinite(displayedPosition) &&
                        (showProjectedPosition || Math.abs(displayedPosition) > 0);

                      return (
                        <React.Fragment key={res.id}>
                          <div
                            role="button"
                            tabIndex={isSuspended ? -1 : 0}
                            aria-label={`${res.selectionName}, odds ${res.odds}`}
                            aria-pressed={Boolean(isSelected)}
                            aria-disabled={isSuspended}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                event.currentTarget.click();
                              }
                            }}
                            style={{
                              ...sportsbookRowStyles.row,
                              ...(isOddEven ? sportsbookRowStyles.card : {}),
                              cursor: isSuspended ? "not-allowed" : "pointer",
                            }}
                            onClick={() => {
                              if (isSuspended) return;
                              if (
                                String(item.marketStatus) !== "1" &&
                                String(item.marketStatus) !== "0"
                              ) return;

                              // Feed-sourced premium markets don't carry a
                              // `sport_setting` object, so a positive
                              // `== "active"` gate silently swallowed every
                              // click (bet slip never opened). Mirror the
                              // other market components (Fancy/Bookmaker):
                              // allow the click unless the sport is explicitly
                              // inActive.
                              if (details?.sport_setting?.status != "inActive") {
                                if (details.premiumFancy == "on") {
                                  if (parly) {
                                    setParlyBet((current) => {
                                      let newObj = {
                                        ...item,
                                        sportsBookSelectionObject: res,
                                        selectObj: res?.id,
                                        newEventId: details?.eventId,
                                        eventName: details?.eventName,
                                        marketId: details?.marketId,
                                      };
                                      if (current?.length > 0) {
                                        if (
                                          current?.filter(
                                            (temp) => temp?.newEventId == details?.eventId
                                          )?.length > 0
                                        ) {
                                          if (
                                            current?.filter(
                                              (temp) => temp?.newEventId !== details?.eventId
                                            )?.length > 0
                                          ) {
                                            secureLocalStorage.setItem("parlyBet", [
                                              ...current?.filter(
                                                (temp) => temp?.newEventId !== details?.eventId
                                              ),
                                              newObj,
                                            ]);
                                            return [
                                              ...current?.filter(
                                                (temp) => temp?.newEventId !== details?.eventId
                                              ),
                                              newObj,
                                            ];
                                          } else {
                                            secureLocalStorage.setItem("parlyBet", [newObj]);
                                            return [newObj];
                                          }
                                        } else {
                                          secureLocalStorage.setItem("parlyBet", [...current, newObj]);
                                          return [...current, newObj];
                                        }
                                      } else {
                                        secureLocalStorage.setItem("parlyBet", [newObj]);
                                        return [newObj];
                                      }
                                    });
                                  } else {
                                    if (!messaageBox) {
                                      if (betSelectionObj.betType !== betType) {
                                        resetSelection(betSelectionObj.betType);
                                      }
                                      setSelected(item?.id);
                                      setBetSelectionObj((prev) => ({
                                        ...betSelectionObj,
                                        betType: betType,
                                        oddsSelect: res?.odds,
                                        bidPrice: defaultStake == 0 ? "" : defaultStake,
                                        priceToogle: true,
                                        layorback: "back",
                                        inputType: "price",
                                        expectany: defaultOdds,
                                        minAmount: amountRange.min,
                                        maxAmount: amountRange.max,
                                      }));
                                      setId(item?.id);
                                      setInnerId(res?.id);
                                      setSlipObj({
                                        ...item,
                                        selectId: res.id,
                                        newMarketId: item.id,
                                        obj: res,
                                      });
                                    }
                                  }
                                }
                              }
                            }}
                          >
                            {details?.sport_setting?.status == "inActive" ||
                            item.marketStatus == 3 ||
                            item.apiSiteStatus == "SUSPENDED" ? (
                              <div style={sportsbookRowStyles.suspension}>
                                <strong>Suspended</strong>
                              </div>
                            ) : item?.marketStatus == 1 ? (
                              ""
                            ) : (
                              <div style={sportsbookRowStyles.suspension}>
                                <strong>
                                  {item?.marketStatus == 9
                                    ? "Ball Running"
                                    : item?.marketStatus == 2
                                      ? "Suspended"
                                      : item?.marketStatus == 3
                                        ? "Suspended"
                                        : item?.marketStatus == 4
                                          ? "Closed"
                                          : ""}
                                </strong>
                              </div>
                            )}

                            <div style={{
                              ...sportsbookRowStyles.selection,
                              ...(isOddEven ? sportsbookRowStyles.cardSelection : {}),
                            }}>
                              <span style={sportsbookRowStyles.selectionName}>
                                {res.selectionName}
                              </span>
                              {showPosition && (
                                <span
                                  aria-label={showProjectedPosition ? "Projected profit/loss" : "Profit/loss"}
                                  style={{
                                    ...sportsbookRowStyles.position,
                                    color: displayedPosition >= 0 ? "#228b22" : "#f00000",
                                  }}
                                >
                                  ({displayedPosition.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })})
                                </span>
                              )}
                            </div>
                            <OddsCell
                              res={res}
                              active={Boolean(isSelected)}
                              marketLimit={item?.markets?.max ?? item?.max}
                              card={isOddEven}
                            />
                          </div>
                          {!isOddEven && res.id == innerId && betslip}
                        </React.Fragment>
                      );
                    })}
                {isOddEven && betslip}
              </div>
            </div>
          );
        }
      })}
    </>
  );
};

const sportsbookRowStyles = {
  list: {
    display: "block", width: "100%", margin: 0, padding: 0,
  },
  oddEvenList: {
    display: "flex", flexWrap: "wrap", boxSizing: "border-box",
    background: "linear-gradient(90deg, #96dfb5 0%, #cde5dc 50%, #96dfb5 100%)",
    borderBottom: "1px solid #7e97a7",
  },
  card: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "flex-start", flex: "0 0 50%", width: "50%", minWidth: 0,
    minHeight: "70px", gap: "3px", padding: "6px 0 5px",
    background: "transparent", borderBottom: 0,
  },
  cardSelection: {
    width: "100%", padding: "0 4px", boxSizing: "border-box", textAlign: "center",
  },
  cardOdds: {
    alignSelf: "center", width: "57%", minHeight: "44px",
    border: "1px solid #fff", borderRadius: "3px",
  },
  row: {
    position: "relative", display: "grid",
    gridTemplateColumns: "minmax(0, 65fr) minmax(0, 35fr)",
    alignItems: "stretch", width: "100%", minHeight: "44px",
    boxSizing: "border-box", margin: 0, padding: 0,
    background: "#fff", borderBottom: "1px solid #7e97a7", borderRadius: 0,
    fontFamily: "Tahoma, Helvetica, sans-serif", lineHeight: 1.2,
  },
  selection: {
    display: "flex", flexDirection: "column", justifyContent: "center",
    gap: "4px", minWidth: 0, padding: "5px 6px", textAlign: "left",
  },
  selectionName: {
    display: "block", margin: 0, padding: 0, color: "#292d30",
    fontSize: "12px", fontWeight: 700, lineHeight: 1.2,
    whiteSpace: "normal", overflowWrap: "anywhere",
  },
  position: {
    display: "block", margin: 0, padding: 0, background: "transparent",
    fontSize: "12px", fontWeight: 700, lineHeight: 1.2,
    overflowWrap: "anywhere",
  },
  odds: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", alignSelf: "stretch", gap: "2px",
    minWidth: 0, margin: 0, padding: "3px 2px", boxSizing: "border-box",
    borderLeft: "1px solid #fff", textAlign: "center",
  },
  oddsValue: {
    display: "block", margin: 0, padding: 0,
    fontSize: "14px", fontWeight: 700, lineHeight: 1.2,
  },
  oddsAmount: {
    display: "block", margin: 0, padding: 0,
    fontSize: "12px", fontWeight: 400, lineHeight: 1.2,
  },
  suspension: {
    position: "absolute", inset: 0, zIndex: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "#00000017", color: "#243a48", fontSize: "12px",
  },
};

export default PreminumFancyNew;